import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dropdown, Input, Modal, Select, Skeleton, message } from "antd";
import { DeleteOutlined, EditOutlined, MoreOutlined } from "@ant-design/icons";
import BrandTemplateApi, {
  BrandTemplateDto,
  BrandTemplateProductType,
  BrandTemplateSizeUnit,
} from "../../../../../api/BrandTemplateApi";
import { Button as MhmButton } from "../../../misc/Button";
import { env } from "../../../../../env/env";
import RendererThumb from "../../../../common/RendererThumb";
import "./TemplatesTab.scss";

const PRODUCT_TYPE_TO_SIZE_KEY: Record<BrandTemplateProductType, string> = {
  MENU_LETTER: "menu-letter",
  MENU_LEGAL: "menu-legal",
  MENU_TABLOID: "menu-tabloid",
  MENU_A4: "menu-a4",
  MENU_CUSTOM_SIZE: "custom",
  DMB_LANDSCAPE: "digital-menu-board",
  DMB_PORTRAIT: "digital-menu-board-portrait",
};

type Mode = "saved" | "create";

interface TemplateOption {
  productType: BrandTemplateProductType;
  label: string;
  thumb:
    | "menu"
    | "menu-tabloid"
    | "menu-custom"
    | "menu-letter"
    | "menu-legal"
    | "menu-a4"
    | "dmb-landscape"
    | "dmb-portrait";
}

const MENU_OPTIONS: TemplateOption[] = [
  { productType: "MENU_CUSTOM_SIZE", label: "Custom Size", thumb: "menu-custom" },
  { productType: "MENU_LETTER", label: "Letter Menu", thumb: "menu-letter" },
  { productType: "MENU_LEGAL", label: "Legal Menu", thumb: "menu-legal" },
  { productType: "MENU_TABLOID", label: "Tabloid Menu", thumb: "menu-tabloid" },
  { productType: "MENU_A4", label: "A4 Menu", thumb: "menu-a4" },
];

const DMB_OPTIONS: TemplateOption[] = [
  { productType: "DMB_LANDSCAPE", label: "Landscape", thumb: "dmb-landscape" },
  { productType: "DMB_PORTRAIT", label: "Portrait", thumb: "dmb-portrait" },
];

const PRODUCT_LABELS: Record<BrandTemplateProductType, string> = {
  MENU_CUSTOM_SIZE: "Custom Size Menu",
  MENU_LETTER: "Letter Menu",
  MENU_LEGAL: "Legal Menu",
  MENU_TABLOID: "Tabloid Menu",
  MENU_A4: "A4 Menu",
  DMB_LANDSCAPE: "Landscape DMB",
  DMB_PORTRAIT: "Portrait DMB",
};

function TemplateThumb({ variant }: { variant: TemplateOption["thumb"] }) {
  switch (variant) {
    case "menu-custom":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/custom-size.png`}
          alt="Custom size menu"
          className="template-thumb-img"
        />
      );
    case "menu-letter":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/letter-menu.png`}
          alt="Letter menu"
          className="template-thumb-img"
        />
      );
    case "menu-legal":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/legal-menu.png`}
          alt="Legal menu"
          className="template-thumb-img"
        />
      );
    case "menu-a4":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/a4-menu.svg`}
          alt="A4 menu"
          className="template-thumb-img"
        />
      );
    case "menu-tabloid":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/tabloid-menu.png`}
          alt="Tabloid menu"
          className="template-thumb-img"
        />
      );
    case "dmb-landscape":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/dmb-landscape.png`}
          alt="Landscape digital menu board"
          className="template-thumb-img"
        />
      );
    case "dmb-portrait":
      return (
        <img
          src={`${env.dashboardPath}/brand-templates/dmb-portrait.png`}
          alt="Portrait digital menu board"
          className="template-thumb-img template-thumb-img--portrait"
        />
      );
    case "menu":
    default:
      return (
        <svg viewBox="0 0 80 110" className="template-thumb-svg template-thumb-svg--portrait">
          <rect x="10" y="8" width="60" height="94" rx="2" fill="#fff" stroke="#333" strokeWidth="1" />
          <rect x="22" y="18" width="36" height="6" rx="1" fill="#bbb" />
          <rect x="16" y="34" width="48" height="2" fill="#333" />
          <rect x="16" y="42" width="36" height="2" fill="#bbb" />
          <rect x="56" y="42" width="8" height="2" fill="#333" />
          <rect x="16" y="50" width="36" height="2" fill="#bbb" />
          <rect x="56" y="50" width="8" height="2" fill="#333" />
          <rect x="16" y="58" width="36" height="2" fill="#bbb" />
          <rect x="56" y="58" width="8" height="2" fill="#333" />
          <rect x="16" y="66" width="36" height="2" fill="#bbb" />
          <rect x="56" y="66" width="8" height="2" fill="#333" />
          <rect x="16" y="82" width="36" height="2" fill="#bbb" />
          <rect x="56" y="82" width="8" height="2" fill="#333" />
        </svg>
      );
  }
}

function SavedTemplateThumb({
  id,
  name,
  fallbackVariant,
}: {
  id: string | undefined;
  name: string;
  fallbackVariant: TemplateOption["thumb"];
}) {
  return (
    <RendererThumb
      id={id}
      alt={name}
      className="template-thumb-img"
      fallback={<TemplateThumb variant={fallbackVariant} />}
    />
  );
}

interface Props {
  brandKitId: string;
}

export function TemplatesTab({ brandKitId }: Props) {
  const [mode, setMode] = useState<Mode>("saved");
  const [templates, setTemplates] = useState<BrandTemplateDto[]>([]);
  const [loading, setLoading] = useState(false);

  const [customSizeOpen, setCustomSizeOpen] = useState(false);
  const [customWidth, setCustomWidth] = useState("");
  const [customHeight, setCustomHeight] = useState("");
  const [customUnit, setCustomUnit] = useState<BrandTemplateSizeUnit>("IN");
  const [customSubmitting, setCustomSubmitting] = useState(false);

  const [renameTarget, setRenameTarget] = useState<BrandTemplateDto | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameSaving, setRenameSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<BrandTemplateDto | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    if (!brandKitId) return;
    setLoading(true);
    BrandTemplateApi.listByBrandKit(brandKitId)
      .then((resp) => setTemplates(resp.data || []))
      .catch(() => message.error("Failed to load templates"))
      .finally(() => setLoading(false));
  }, [brandKitId]);

  useEffect(() => {
    load();
  }, [load]);

  const redirectToNewBrandTemplate = (
    productType: BrandTemplateProductType,
    custom?: { width: number; height: number; unit: BrandTemplateSizeUnit }
  ) => {
    const params = new URLSearchParams();
    params.set("newBrandTemplate", "true");
    params.set("brandKitId", brandKitId);
    params.set("size", PRODUCT_TYPE_TO_SIZE_KEY[productType]);
    if (custom) {
      params.set("width", String(custom.width));
      params.set("height", String(custom.height));
      params.set("unit", custom.unit.toLowerCase());
    }
    window.location.href = `${env.timberContextPath}/design?${params.toString()}`;
  };

  const handleTileClick = (opt: TemplateOption) => {
    if (opt.productType === "MENU_CUSTOM_SIZE") {
      setCustomWidth("");
      setCustomHeight("");
      setCustomUnit("IN");
      setCustomSizeOpen(true);
      return;
    }
    redirectToNewBrandTemplate(opt.productType);
  };

  const handleCustomSizeCreate = () => {
    const widthNum = parseFloat(customWidth);
    const heightNum = parseFloat(customHeight);
    const min = customUnit === "PX" ? 96 : 1;
    const max = customUnit === "PX" ? 8000 : 83.328;
    const errorMessage =
      customUnit === "PX"
        ? "Dimensions must be between 96px and 8000px."
        : "Dimensions must be between 1in and 83.328in.";
    const isInvalid = (n: number) => !Number.isFinite(n) || n < min || n > max;
    if (isInvalid(widthNum) || isInvalid(heightNum)) {
      message.error(errorMessage);
      return;
    }
    setCustomSubmitting(true);
    redirectToNewBrandTemplate("MENU_CUSTOM_SIZE", {
      width: widthNum,
      height: heightNum,
      unit: customUnit,
    });
  };

  const sanitizeNumericInput = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const firstDot = cleaned.indexOf(".");
    if (firstDot === -1) return cleaned;
    return (
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "")
    );
  };

  const openInEditor = (template: BrandTemplateDto) => {
    if (!template.id) return;
    window.location.href = `${env.timberContextPath}/design?id=${encodeURIComponent(template.id)}`;
  };

  const handleRename = (template: BrandTemplateDto) => {
    if (!template.id) return;
    setRenameValue(template.name);
    setRenameTarget(template);
  };

  const closeRename = () => {
    if (renameSaving) return;
    setRenameTarget(null);
    setRenameValue("");
  };

  const submitRename = async () => {
    if (!renameTarget?.id) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      message.error("Name is required");
      return;
    }
    setRenameSaving(true);
    try {
      const resp = await BrandTemplateApi.rename(renameTarget.id, trimmed);
      if (!resp.ok) {
        message.error("Failed to rename template");
        return;
      }
      setTemplates((prev) =>
        prev.map((t) => (t.id === renameTarget.id ? { ...t, name: trimmed } : t))
      );
      message.success("Template renamed");
      setRenameTarget(null);
      setRenameValue("");
    } finally {
      setRenameSaving(false);
    }
  };

  const handleDelete = (template: BrandTemplateDto) => {
    if (!template.id) return;
    setDeleteTarget(template);
  };

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
  };

  const submitDelete = async () => {
    if (!deleteTarget?.id) return;
    setDeleting(true);
    try {
      const resp = await BrandTemplateApi.delete(deleteTarget.id);
      if (!resp.ok) {
        message.error("Failed to delete template");
        return;
      }
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
      message.success("Template deleted");
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  const saved = useMemo(() => templates, [templates]);

  return (
    <div className="templates-tab">
      {mode === "create" ? (
        <>
          <button
            type="button"
            className="templates-back-link"
            onClick={() => setMode("saved")}
          >
            &lt; Back
          </button>

          <h3 className="templates-section-heading">Create New...</h3>

          <section className="templates-section">
            <h4>Menu</h4>
            <div className="templates-grid">
              {MENU_OPTIONS.map((opt) => (
                <button
                  key={opt.productType}
                  type="button"
                  className="template-tile"
                  onClick={() => handleTileClick(opt)}
                >
                  <div className="template-tile-label">{opt.label}</div>
                  <div className="template-tile-thumb">
                    <TemplateThumb variant={opt.thumb} />
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="templates-section">
            <h4>Digital Menu Board</h4>
            <div className="templates-grid">
              {DMB_OPTIONS.map((opt) => (
                <button
                  key={opt.productType}
                  type="button"
                  className="template-tile"
                  onClick={() => handleTileClick(opt)}
                >
                  <div className="template-tile-label">{opt.label}</div>
                  <div className="template-tile-thumb">
                    <TemplateThumb variant={opt.thumb} />
                  </div>
                </button>
              ))}
            </div>
          </section>
        </>
      ) : (
        <>
          <div className="templates-saved-header">
            <h3 className="templates-section-heading">Saved Brand Templates</h3>
            {saved.length > 0 && (
              <button
                type="button"
                className="templates-new-btn"
                onClick={() => setMode("create")}
              >
                + New Brand Template
              </button>
            )}
          </div>

          {loading ? (
            <Skeleton active />
          ) : saved.length === 0 ? (
            <div className="templates-empty">
              <p>You don&apos;t have any saved brand templates yet.</p>
              <button
                type="button"
                className="templates-new-btn"
                onClick={() => setMode("create")}
              >
                + New Brand Template
              </button>
            </div>
          ) : (
            <div className="templates-saved-grid">
              {saved.map((t) => {
            const variant: TemplateOption["thumb"] =
              t.productType === "MENU_TABLOID"
                ? "menu-tabloid"
                : t.productType === "MENU_CUSTOM_SIZE"
                ? "menu-custom"
                : t.productType === "MENU_LETTER"
                ? "menu-letter"
                : t.productType === "MENU_LEGAL"
                ? "menu-legal"
                : t.productType === "MENU_A4"
                ? "menu-a4"
                : t.productType === "DMB_LANDSCAPE"
                ? "dmb-landscape"
                : t.productType === "DMB_PORTRAIT"
                ? "dmb-portrait"
                : "menu";
            return (
              <div key={t.id} className="template-saved-card">
                <Dropdown
                  className="desktop-design-menu more-btn-ops"
                  trigger={["click"]}
                  overlayClassName="template-saved-card-menu-overlay"
                  menu={{
                    items: [
                      {
                        key: "rename",
                        icon: <EditOutlined />,
                        label: "Rename",
                        onClick: () => handleRename(t),
                      },
                      {
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: "Delete",
                        onClick: () => handleDelete(t),
                      },
                    ],
                  }}
                >
                  <MoreOutlined />
                </Dropdown>
                <div
                  className="template-saved-card-thumb"
                  role="button"
                  tabIndex={0}
                  onClick={() => openInEditor(t)}
                >
                  <SavedTemplateThumb id={t.id} name={t.name} fallbackVariant={variant} />
                </div>
                <div className="template-saved-card-footer">
                  <div className="template-saved-card-text">
                    <div className="template-saved-card-name" title={t.name}>
                      {t.name}
                    </div>
                    <div className="template-saved-card-type">
                      {PRODUCT_LABELS[t.productType]}
                    </div>
                    {t.lastEditorName && (
                      <div className="template-saved-card-editor" title={`Last edited by ${t.lastEditorName}`}>
                        Edited by {t.lastEditorName}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
            </div>
          )}
        </>
      )}

      <Modal
        className="new-brand-modal clean one template-custom-size-modal"
        open={customSizeOpen}
        onCancel={() => {
          if (customSubmitting) return;
          setCustomSizeOpen(false);
        }}
        footer={null}
        maskClosable={!customSubmitting}
      >
        <header>
          <h2>Create custom size</h2>
        </header>

        <div className="template-custom-size-body">
          <Input
            size="large"
            placeholder="Width"
            value={customWidth}
            onChange={(e) => setCustomWidth(sanitizeNumericInput(e.target.value))}
            inputMode="decimal"
            autoFocus
          />
          <span className="template-custom-size-x">×</span>
          <Input
            size="large"
            placeholder="Height"
            value={customHeight}
            onChange={(e) => setCustomHeight(sanitizeNumericInput(e.target.value))}
            onPressEnter={handleCustomSizeCreate}
            inputMode="decimal"
          />
          <Select
            size="large"
            className="template-custom-size-unit"
            value={customUnit}
            onChange={setCustomUnit}
            options={[
              { value: "IN", label: "in" },
              { value: "PX", label: "px" },
            ]}
          />
        </div>

        <footer>
          <MhmButton
            type="Cancel"
            onClick={() => setCustomSizeOpen(false)}
            disabled={customSubmitting}
          >
            <span>Cancel</span>
          </MhmButton>
          <MhmButton
            type="Primary"
            onClick={handleCustomSizeCreate}
            loading={customSubmitting}
            disabled={!customWidth.trim() || !customHeight.trim()}
          >
            <span>Create</span>
          </MhmButton>
        </footer>
      </Modal>

      <Modal
        className="new-brand-modal clean one"
        open={!!renameTarget}
        onCancel={closeRename}
        footer={null}
        maskClosable={!renameSaving}
      >
        <header>
          <h2>Rename brand template</h2>
        </header>

        <div className="new-brand-body">
          <Input
            size="large"
            placeholder="Template name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onPressEnter={submitRename}
            autoFocus
          />
        </div>

        <footer>
          <MhmButton type="Cancel" onClick={closeRename} disabled={renameSaving}>
            <span>Cancel</span>
          </MhmButton>
          <MhmButton
            type="Primary"
            onClick={submitRename}
            loading={renameSaving}
            disabled={!renameValue.trim()}
          >
            <span>Save</span>
          </MhmButton>
        </footer>
      </Modal>

      <Modal
        className="clean one"
        open={!!deleteTarget}
        onCancel={closeDelete}
        footer={null}
        maskClosable={!deleting}
      >
        <header>
          <h2>Are you sure?</h2>
        </header>

        <p>
          <b>{deleteTarget?.name || "This brand template"}</b> will be permanently deleted.
          Designs created from this template will not be affected.
        </p>

        <footer>
          <Button size="large" onClick={closeDelete} disabled={deleting}>
            Cancel
          </Button>
          <Button
            size="large"
            className="primary"
            onClick={submitDelete}
            loading={deleting}
          >
            Delete
          </Button>
        </footer>
      </Modal>
    </div>
  );
}
